// The report side of funds (PRD 83 Wave 2, §9): one fund filter on the
// journal register and the P&L, in the property filter's modes.
//
// Pure helpers only — the wire params, the URL param that keeps a filtered
// report shareable, and the header label. No filter is always `null` and
// always sends nothing, so an unfiltered request is exactly what it was before
// funds existed.

import { BusinessApiError } from '../../../services/businessApiCommon';
import { FUND_ERROR_CODES, Fund, ReportFundSelection } from '../../../types/fundsApi';

/** A selection that filters nothing (no ids) is no filter. */
export function normalizeFundSelection(
  selection: ReportFundSelection | null | undefined
): ReportFundSelection | null {
  if (!selection) return null;
  if (selection.mode === 'ids' && selection.ids.length === 0) return null;
  return selection;
}

/** The P&L's params: `fund_ids` comma-joined (the api splits it) or `fund_unassigned`. */
export function fundFilterQueryParams(
  selection: ReportFundSelection | null | undefined
): Record<string, string | undefined> {
  const filter = normalizeFundSelection(selection);
  if (!filter) return {};
  if (filter.mode === 'unassigned') return { fund_unassigned: 'true' };
  return { fund_ids: filter.ids.join(',') };
}

/**
 * The journal register's params. The register takes ONE fund (`fund_id`, a
 * line dimension beside customer and vendor) or `fund_unassigned`.
 */
export function registerFundQueryParams(
  selection: ReportFundSelection | null | undefined
): Record<string, string | undefined> {
  const filter = normalizeFundSelection(selection);
  if (!filter) return {};
  if (filter.mode === 'unassigned') return { fund_unassigned: 'true' };
  return { fund_id: filter.ids[0] };
}

// ---- URL param ------------------------------------------------------------
// One `fund` search param: "unassigned" or "<id>,<id>". Unreadable is none.

export const FUND_URL_PARAM = 'fund';

export function parseFundParam(value: string | null): ReportFundSelection | null {
  if (!value) return null;
  if (value === 'unassigned') return { mode: 'unassigned' };
  const ids = value
    .split(',')
    .map(id => id.trim())
    .filter(Boolean);
  return ids.length > 0 ? { mode: 'ids', ids } : null;
}

export function fundParamValue(selection: ReportFundSelection | null | undefined): string | undefined {
  const filter = normalizeFundSelection(selection);
  if (!filter) return undefined;
  return filter.mode === 'unassigned' ? 'unassigned' : filter.ids.join(',');
}

// ---- Labels -----------------------------------------------------------------

/** "Fund: Grants, Operating Fund" / "Fund: Unassigned only"; null for none. */
export function fundSelectionLabel(
  selection: ReportFundSelection | null | undefined,
  funds: Pick<Fund, 'id' | 'name'>[]
): string | null {
  const filter = normalizeFundSelection(selection);
  if (!filter) return null;
  if (filter.mode === 'unassigned') return 'Fund: Unassigned only';
  const names = filter.ids.map(id => funds.find(fund => fund.id === id)?.name);
  if (names.some(name => !name)) {
    return `Fund: ${filter.ids.length} ${filter.ids.length === 1 ? 'fund' : 'funds'}`;
  }
  return `${filter.ids.length === 1 ? 'Fund' : 'Funds'}: ${names.join(', ')}`;
}

/**
 * The api refuses a filter rather than drop it. Say so, and what to do; any
 * other error keeps the server's sentence, a non-api failure the fallback.
 */
export function fundAwareErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof BusinessApiError)) return fallback;
  if (error.code === FUND_ERROR_CODES.invalidFilter) {
    return `The fund filter was refused: ${error.message}. Clear it or pick other funds.`;
  }
  return error.message;
}
